import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { motion } from 'framer-motion';
import { Calendar, Globe, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getNewsPageData, getNewsDescriptions, formatNewsPageData, formatNewsDescriptions } from '../api/strapi/newsPageApi';
import SectionGuard from '../components/SectionGuard';
import { useLanguage } from '../context';
import { getLocalizedPath } from '../helpers/routes';
import DataLoader from '../components/loader/DataLoader';
import PageHero from '../components/common/PageHero';
import SectionHeader from '../components/common/SectionHeader';
import SkeletonImage from '../components/common/SkeletonImage';

const NewsPage = () => {
  const intl = useIntl();
  const [news, setNews] = useState([]);
  const [featuredNews, setFeaturedNews] = useState(null);
  const [pageData, setPageData] = useState(null);
  const [pageResult, setPageResult] = useState(null);
  const [newsResult, setNewsResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const { language } = useLanguage();

  // Fetch data from APIs
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch page data and news articles separately to handle potential errors independently
        const [pageResultData, newsListResult] = await Promise.all([
          getNewsPageData(language),
          getNewsDescriptions(language),
        ]);

        setPageResult(pageResultData);
        setNewsResult(newsListResult);

        if (!pageResultData.ok && pageResultData.reason !== "NOT_FOUND") {
          console.warn("News page data unavailable:", pageResultData);
        }
        if (!newsListResult.ok && newsListResult.reason !== "NOT_FOUND") {
          console.warn("News list unavailable:", newsListResult);
        }

        const formattedPageData = pageResultData.ok
          ? formatNewsPageData(pageResultData.data)
          : null;
        const formattedNewsData =
          newsListResult.ok && Array.isArray(newsListResult.data)
            ? formatNewsDescriptions(newsListResult.data)
            : [];

        setNews(formattedNewsData);
        setFeaturedNews(formattedNewsData.length > 0 ? formattedNewsData[0] : null);
        setPageData(formattedPageData);
      } catch (error) {
        console.error('Error fetching news page data:', error);
        setNews([]);
        setFeaturedNews(null);
        setPageData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [language]);

  // Generate categories dynamically from news data, only if news data exists
  const allCategories = news && Array.isArray(news) ? [...new Set(news.map(item => item.category).filter(Boolean))] : [];
  const categories = [
    { name: 'all', label: intl.formatMessage({ id: "news.categories.all" }), count: news && Array.isArray(news) ? news.length : 0 },
    ...allCategories.map(category => ({
      name: category,
      label: category,
      count: (news && Array.isArray(news)) ? news.filter(n => n.category === category).length : 0
    }))
  ];

  const filteredNews = (news && Array.isArray(news) ? news : []).filter(newsItem => {
    const matchesCategory = selectedCategory === 'all' || newsItem.category === selectedCategory;
    const matchesSearch = newsItem.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         newsItem.excerpt?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch && featuredNews && newsItem.id !== featuredNews.id;
  });

  // Use stats from API data only, no fallback to prevent errors
  const stats = pageData?.stats || [];

  // Fonction pour tronquer un texte à une certaine longueur
  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const sectionFallback = (
    <div className="py-10 text-center text-gray-500 text-sm">
      {intl.formatMessage({ id: "empty.section" })}
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{pageData?.title ? `${pageData.title} | Africa Systems` : intl.formatMessage({ id: "news.meta.title" })}</title>
        <meta name="description" content={pageData?.heroComponent?.description} />
      </Helmet>

      {/* Hero Section */}
      <SectionGuard result={pageResult} fallback={sectionFallback} logLabel="News hero">
        {pageData?.heroComponent && (
          <PageHero
            eyebrow="Africa Systems"
            title={pageData.heroComponent.title}
            description={pageData.heroComponent.description}
            image={pageData.heroComponent.image || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80'}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="w-full max-w-2xl"
            >
              <div className="relative">
                <input
                  type="text"
                  placeholder={intl.formatMessage({ id: "news.search.placeholder" })}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-6 py-4 pl-12 rounded-full text-gray-900 focus:outline-none focus:ring-4 focus:ring-[#16A34A]"
                />
                <Globe className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              </div>
            </motion.div>
          </PageHero>
        )}
      </SectionGuard>

      {/* Stats Section */}
      <SectionGuard result={pageResult} fallback={null} logLabel="News stats">
        <section className="py-12 bg-neutral-50">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <div className="text-3xl font-bold text-[#0a1628] mb-2">{stat.value}</div>
                  <div className="text-slate-600">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </SectionGuard>

      {/* Featured News */}
      {featuredNews && (
        <section className="py-16">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="group bg-white border border-slate-200 hover:border-[#16A34A]/40 hover:shadow-lg transition-all duration-300 overflow-hidden"
            >
              <div className="lg:flex">
                <div className="lg:w-1/2">
                  {featuredNews.image && (
                    <SkeletonImage
                      src={featuredNews.image}
                      alt={featuredNews.title}
                      className="w-full h-64 lg:h-full"
                      imgClassName="w-full h-full object-cover"
                    />
                  )}
                </div>
                <div className="lg:w-1/2 p-8 lg:p-12 flex flex-col justify-center">
                  <h2 className="text-3xl font-bold text-[#0a1628] mb-4 group-hover:text-[#16A34A] transition-colors">{truncateText(featuredNews.title, 60)}</h2>
                  <p className="text-slate-600 mb-6">{truncateText(featuredNews.excerpt, 150)}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Calendar className="w-5 h-5 text-slate-500 mr-2" />
                      <span className="text-slate-600">{featuredNews.date} • {featuredNews.readTime}</span>
                    </div>
                    <Link
                      to={getLocalizedPath(`/news/${featuredNews.slug || featuredNews.id}`, language)}
                      className="bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold py-2.5 px-6 text-xs uppercase tracking-[0.12em] transition-colors duration-200 inline-block"
                    >
                      {intl.formatMessage({ id: "news.readMore" })}
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Category Filter */}
      <section className="py-8 bg-neutral-50">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex flex-wrap items-center justify-center gap-4">
            <TrendingUp className="w-5 h-5 text-slate-600" />
            {categories.map((category) => (
              <button
                key={category.name}
                onClick={() => setSelectedCategory(category.name)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  selectedCategory === category.name
                    ? 'bg-[#16A34A] text-white'
                    : 'bg-white text-slate-700 hover:bg-slate-100'
                }`}
              >
                {category.label} ({category.count})
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <SectionHeader
            eyebrow="Africa Systems"
            title={intl.formatMessage({ id: "news.recent" })}
            align="center"
          />

          {loading ? (
            <DataLoader isLoading={true} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredNews.map((newsItem, index) => (
                <motion.article
                  key={newsItem.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="group bg-white border border-slate-200 hover:border-[#16A34A]/40 hover:shadow-lg transition-all duration-300 overflow-hidden rounded-xl flex flex-col"
                >
                  {newsItem.image && (
                    <SkeletonImage
                      src={newsItem.image}
                      alt={newsItem.title}
                      className="w-full h-48"
                      imgClassName="w-full h-full object-cover"
                    />
                  )}
                  <div className="p-6 flex flex-col flex-grow">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center text-sm text-slate-500">
                        <Calendar className="w-4 h-4 mr-1" />
                        {newsItem.date}
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-[#0a1628] mb-3 line-clamp-2 group-hover:text-[#16A34A] transition-colors">{truncateText(newsItem.title, 50)}</h3>
                    <p className="text-slate-600 mb-4 line-clamp-3">{truncateText(newsItem.excerpt, 100)}</p>
                    <div className="flex items-center justify-between mt-auto">
                      {newsItem.readTime && (
                        <span className="text-sm text-slate-500">{newsItem.readTime}</span>
                      )}
                      <Link to={getLocalizedPath(`/news/${newsItem.slug || newsItem.id}`, language)} className="inline-flex items-center gap-2 text-[#16A34A] hover:text-[#15803D] font-semibold text-sm">
                        {intl.formatMessage({ id: "news.readMoreShort" })}
                      </Link>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Press Kit CTA */}
      <SectionGuard result={pageResult} fallback={null} logLabel="News press kit">
        <section className="py-20 bg-[#0a1628]">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                {pageData?.pressKitSection?.title}
              </h2>
              <p className="text-xl text-white/75 max-w-2xl mx-auto mb-8">
                {pageData?.pressKitSection?.description}
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <a href={getLocalizedPath('/contact', language)} className="border-2 border-white text-white hover:bg-white hover:text-[#0a1628] font-semibold py-3 px-8 rounded-lg transition-colors duration-200">
                  {intl.formatMessage({ id: "news.contactTeam" })}
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </SectionGuard>
    </div>
  );
};

export default NewsPage;
